import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { formatPaymentDate } from '@futrli/organisations/core/utils';
import { useErrorHandler } from '@futrli/errors';
import { useLoadDataRequest } from '@futrli/shared-services';
import { Workspace, WorkspacesListingItem } from '@futrli-sdk/workspaces';

import { fetchEstimates } from '../../../core/api';

type RequestDataType = Awaited<ReturnType<typeof fetchEstimates>>;

/**
 * Hook that returns estimate text and loading state, used when viewing the 'Add an organisation' modal
 * @param workspace - Current workspace data
 * @param isOpen - Whether the 'Add an organisation' modal is open or not. Passing this flag will cause
 *                 the data to refresh every time the modal is opened (for a non-trial workspace).
 */
export const useAddOrgEstimate = (workspace: Workspace | WorkspacesListingItem | null | undefined, isOpen = true) => {
  const intl = useIntl();
  const onManageError = useErrorHandler();
  const { load, data, isLoading, error } = useLoadDataRequest<RequestDataType>();

  const isTrialWorkspace = workspace?.subscription_state === 'trial';

  const estimatesText = useMemo(() => {
    if (isTrialWorkspace) {
      return intl.formatMessage({
        id: 'organisations.add_as_many_organisations_as_you_like',
        defaultMessage:
          "You may add as many organisations as you like while in trial. Once your trial ends, we'll charge you per organisation."
      });
    } else if (data) {
      if (data.current_plan_amount === data.projected_plan_amount) {
        return intl.formatMessage(
          {
            id: 'organisations.adding_organisations_with_no_charge',
            defaultMessage:
              'You have {current_plan_amount, number} {current_plan_amount, plural, one {licence} other {licences}} on your subscription and may add {diff} more {diff, plural, one {organisation} other {organisations}} with no further charge.'
          },
          {
            current_plan_amount: data.current_plan_amount,
            diff: data.current_plan_amount - data.current_organisation_count
          }
        );
      } else {
        return intl.formatMessage(
          {
            id: 'organisations.adding_organisation_with_charge',
            defaultMessage:
              'Adding another organisation will incur a charge of {todayValue} today to cover the remainder of your billing period. Your next invoice on {nextPaymentDate} will then be {nextValue}.'
          },
          {
            todayValue: intl.formatNumber(data.invoices.immediate.amount_due / 100, {
              currency: data.invoices.immediate.currency_code!,
              style: 'currency'
            }),
            nextValue: intl.formatNumber(data.invoices.next_with_change.amount_due / 100, {
              currency: data.invoices.immediate.currency_code!,
              style: 'currency'
            }),
            nextPaymentDate:
              intl.locale === 'en'
                ? formatPaymentDate(data.invoices.next_invoice_date)
                : intl.formatDate(data.invoices.next_invoice_date, { month: 'long', day: 'numeric' })
          }
        );
      }
    }
  }, [data, intl, isTrialWorkspace]);

  // (Re)load estimate data when modal is opened
  useEffect(() => {
    if (workspace?.id && isOpen && !isTrialWorkspace) {
      load(fetchEstimates(workspace.id));
    }
  }, [isOpen, isTrialWorkspace, load, workspace?.id]);

  // Handle errors
  useEffect(() => {
    if (error) {
      onManageError(error);
    }
  }, [error, onManageError]);

  return {
    estimatesText,
    isLoading
  };
};
